import React, { useState, useEffect } from 'react';
import { Tabs, DatePicker, Select, Card, Row, Col, theme, message } from 'antd';
import { LineChartOutlined, BarChartOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import ViewerPerformance from './ViewerPerformance';
import PACSServerMetrics from './PACSServerMetrics';
import { makeGetCall } from '../../utils/helper';
import './dashboard.css';

const { RangePicker } = DatePicker;

// Styled Tabs Component matching customApp.tsx pattern
const StyledTabs = ({ onTabClick, activeKey, items, tabBarExtraContent }) => {
  const { token } = theme.useToken();

  return (
    <Tabs
      onTabClick={onTabClick}
      activeKey={activeKey}
      style={{ background: 'white' }}
      items={items}
      tabBarExtraContent={tabBarExtraContent}
      tabBarStyle={{
        '--primary-color': token.colorPrimary
      } as React.CSSProperties}
    />
  );
};

const StatsDashboard = () => {
  // Check if aggregated stats view is enabled (dev/analytics server only)
  const isAggregatedStatsEnabled = window.ENV_CONFIG?.ENABLE_AGGREGATED_STATS || false;

  // Date range state - default 30 days
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(30, 'days'),
    dayjs()
  ]);

  // Modality filter state
  const [selectedModality, setSelectedModality] = useState('ALL');

  // Center filter state (only used when aggregated stats enabled)
  const [selectedCenter, setSelectedCenter] = useState('ALL');

  // Active tab state
  const [activeTab, setActiveTab] = useState('viewer');

  // Modality options state
  const [modalityOptions, setModalityOptions] = useState([
    { label: 'All Modalities', value: 'ALL' }
  ]);

  // Center options state (only loaded when aggregated stats enabled)
  const [centerOptions, setCenterOptions] = useState([
    { label: 'All Centers', value: 'ALL' }
  ]);

  // Fetch modalities on component mount
  useEffect(() => {
    const fetchModalities = async () => {
      try {
        const response = await makeGetCall('/modalities');
        if (response?.data?.success && response?.data?.data) {
          // Add "All Modalities" as the first option
          const options = [
            { label: 'All Modalities', value: 'ALL' },
            ...response.data.data
          ];
          setModalityOptions(options);

          // Check if MR modality exists and set it as default
          const mrModality = response.data.data.find(
            (mod: any) => mod.value === 'MR' || mod.value === 'MRI'
          );
          if (mrModality) {
            setSelectedModality(mrModality.value);
          }
        }
      } catch (error) {
        console.error('Error fetching modalities:', error);
        message.error('Failed to load modalities');
      }
    };

    fetchModalities();
  }, []);

  // Fetch centers if aggregated stats is enabled
  useEffect(() => {
    if (!isAggregatedStatsEnabled) {
      return; // Skip if not enabled
    }

    const fetchCenters = async () => {
      try {
        const response = await makeGetCall('/get-centers');
        if (response?.data?.success && response?.data?.data) {
          const options = [
            { label: 'All Centers', value: 'ALL' },
            ...response.data.data.map((center: any) => ({
              label: `${center.center_name} (${center.center_id})`,
              value: center.center_id
            }))
          ];
          setCenterOptions(options);
        }
      } catch (error) {
        console.error('Error fetching centers:', error);
        message.error('Failed to load centers');
      }
    };

    fetchCenters();
  }, [isAggregatedStatsEnabled]);

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setDateRange(dates);
    }
  };

  const handleModalityChange = (value) => {
    setSelectedModality(value);
  };

  const handleCenterChange = (value) => {
    setSelectedCenter(value);
  };

  const handleRefresh = () => {
    // Trigger refresh by updating a key or state
    // The child components will re-fetch when dateRange or modality changes
    setDateRange([...dateRange]); // Force re-render
  };

  const tabItems = [
    {
      key: 'viewer',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <LineChartOutlined />
          <span>Viewer Performance</span>
        </span>
      ),
      children: (
        <ViewerPerformance
          dateRange={dateRange}
          selectedModality={selectedModality}
          selectedCenter={selectedCenter}
          isAggregatedStatsEnabled={isAggregatedStatsEnabled}
        />
      ),
    },
    {
      key: 'pacs',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <BarChartOutlined />
          <span>PACS Server Metrics</span>
        </span>
      ),
      children: (
        <PACSServerMetrics
          dateRange={dateRange}
          selectedModality={selectedModality}
        />
      ),
    },
  ];

  return (
    <div className="stats-dashboard-container">
      <div className="dashboard-header">
        <h2 className="dashboard-title">Analytics Dashboard</h2>
        <p className="dashboard-subtitle">Monitor PACS performance and loading metrics</p>
      </div>

      <div className="dashboard-filters">
        <Row gutter={16} align="middle">
          <Col>
            <label className="filter-label">Date Range:</label>
          </Col>
          <Col>
            <RangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              format="DD/MM/YYYY"
              allowClear={false}
            />
          </Col>
          <Col>
            <label className="filter-label">Modality:</label>
          </Col>
          <Col>
            <Select
              value={selectedModality}
              onChange={handleModalityChange}
              options={modalityOptions}
              style={{ width: 180 }}
            />
          </Col>
          {isAggregatedStatsEnabled && (
            <>
              <Col>
                <label className="filter-label">Center:</label>
              </Col>
              <Col>
                <Select
                  value={selectedCenter}
                  onChange={handleCenterChange}
                  options={centerOptions}
                  style={{ width: 250 }}
                />
              </Col>
            </>
          )}
        </Row>
      </div>

      <div className="dashboard-content">
        <StyledTabs
          onTabClick={(key) => setActiveTab(key)}
          activeKey={activeTab}
          items={tabItems}
          tabBarExtraContent={
            <button
              className="btn-secondary"
              onClick={handleRefresh}
              style={{
                height: '36px',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <ReloadOutlined style={{ marginRight: '6px' }} />
              Refresh Data
            </button>
          }
        />
      </div>
    </div>
  );
};

export default StatsDashboard;